import { useEffect, useState } from "react";
import { Ban, Loader2, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export default function AdminSecurity() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [banIp, setBanIp] = useState("");
  const [banHours, setBanHours] = useState("24");
  const [banReason, setBanReason] = useState("");
  const [whiteIp, setWhiteIp] = useState("");
  const [whiteNote, setWhiteNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/security/overview").then(({ data }) => setData(data.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  const card = "rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6";
  const lbl = "font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400";
  const inputCls = "rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors font-mono-t";

  return (
    <div data-testid="admin-security-page" className="mt-10">
      <h2 className="font-display font-bold text-2xl text-slate-900">{t("sec.title")}</h2>
      <p className="mt-1 text-sm text-slate-500 max-w-2xl">{t("sec.sub")}</p>
      <p data-testid="sec-autoban-info" className="mt-3 inline-block rounded-full bg-[#FAF5EE] px-4 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {t("sec.autobanInfo")}
      </p>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        {/* BAN */}
        <div className={card}>
          <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
            <ShieldOff className="w-5 h-5 text-red-500" /> {t("sec.bans")}
          </h3>
          <div className="flex flex-wrap gap-2 mb-4">
            <input data-testid="sec-ban-ip" value={banIp} onChange={(e) => setBanIp(e.target.value)} placeholder="IP o CIDR" className={`${inputCls} flex-1 min-w-[140px]`} />
            <input data-testid="sec-ban-hours" value={banHours} onChange={(e) => setBanHours(e.target.value)} type="number" min="0" title={t("sec.hours")} className={`${inputCls} w-24`} />
            <input data-testid="sec-ban-reason" value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder={t("sec.reason")} className={`${inputCls} flex-1 min-w-[140px]`} />
            <button data-testid="sec-ban-add" disabled={busy || banIp.trim().length < 3}
              onClick={() => run(() => api.post("/admin/security/ban", { ip: banIp.trim(), hours: parseInt(banHours) || 0, reason: banReason.trim() }), t("admin.saved")).then(() => { setBanIp(""); setBanReason(""); })}
              className="rounded-full bg-red-500 text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-red-600 transition-colors disabled:opacity-50">
              <Ban className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-1.5" data-testid="sec-ban-list">
            {data.bans.length === 0 && <p className="py-4 text-left text-sm text-slate-400">{t("sec.empty")}</p>}
            {data.bans.map((b) => (
              <div key={b.ip} data-testid={`sec-ban-${b.ip}`} className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 ${b.expired ? "bg-slate-50 opacity-60" : "bg-red-50/60"}`}>
                <div className="min-w-0">
                  <div className="font-mono-t text-sm font-bold text-slate-800">{b.ip}</div>
                  <div className="text-xs text-slate-500 truncate">{b.reason} · {b.banned_by} · {b.banned_until ? (b.expired ? t("sec.expired") : `→ ${b.banned_until.slice(0, 16).replace("T", " ")}`) : t("sec.permanent")}</div>
                </div>
                <button data-testid={`sec-unban-${b.ip}`} disabled={busy}
                  onClick={() => run(() => api.delete(`/admin/security/ban/${encodeURIComponent(b.ip)}`))}
                  className="shrink-0 rounded-full border border-slate-200 px-4 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors">
                  {t("sec.unban")}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* WHITELIST */}
        <div className={card}>
          <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> {t("sec.whitelist")}
          </h3>
          <div className="flex flex-wrap gap-2 mb-4">
            <input data-testid="sec-white-ip" value={whiteIp} onChange={(e) => setWhiteIp(e.target.value)} placeholder="IP o CIDR" className={`${inputCls} flex-1 min-w-[140px]`} />
            <input data-testid="sec-white-note" value={whiteNote} onChange={(e) => setWhiteNote(e.target.value)} placeholder={t("sec.note")} className={`${inputCls} flex-1 min-w-[120px]`} />
            <button data-testid="sec-white-add" disabled={busy || whiteIp.trim().length < 3}
              onClick={() => run(() => api.post("/admin/security/whitelist", { ip: whiteIp.trim(), note: whiteNote.trim() }), t("admin.saved")).then(() => { setWhiteIp(""); setWhiteNote(""); })}
              className="rounded-full bg-emerald-600 text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-50">
              +
            </button>
          </div>
          <div className="space-y-1.5" data-testid="sec-white-list">
            {data.whitelist.length === 0 && <p className="py-4 text-left text-sm text-slate-400">{t("sec.empty")}</p>}
            {data.whitelist.map((w) => (
              <div key={w.ip} data-testid={`sec-white-${w.ip}`} className="flex items-center justify-between gap-3 rounded-2xl bg-emerald-50/60 px-4 py-3">
                <div className="min-w-0">
                  <div className="font-mono-t text-sm font-bold text-slate-800">{w.ip}</div>
                  <div className="text-xs text-slate-500 truncate">{w.note || "—"} · {w.added_by}</div>
                </div>
                <button data-testid={`sec-white-del-${w.ip}`} disabled={busy}
                  onClick={() => run(() => api.delete(`/admin/security/whitelist/${encodeURIComponent(w.ip)}`))}
                  className="shrink-0 rounded-full border border-slate-200 p-2 text-slate-400 hover:border-red-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* VIOLAZIONI */}
      <div className={`${card} mt-5`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg text-slate-900">{t("sec.violations")}</h3>
          <span data-testid="sec-stats-24h" className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {t("sec.stats24")}: {data.stats_24h.total} · {data.stats_24h.unique_ips} {t("sec.uniqueIps")}
          </span>
        </div>
        <div className="space-y-1" data-testid="sec-violations-list">
          {data.violations.length === 0 && <p className="py-4 text-left text-sm text-slate-400">{t("sec.empty")}</p>}
          {data.violations.map((v, i) => (
            <div key={`${v.ip}-${v.ts}-${i}`} data-testid={`sec-violation-${i}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-4 py-2.5 hover:bg-[#FAF5EE] transition-colors">
              <span className="font-mono-t text-[10px] text-slate-400 shrink-0">{(v.ts || "").slice(0, 16).replace("T", " ")}</span>
              <span className="font-mono-t text-xs font-bold text-slate-800">{v.ip}</span>
              <span className="rounded-full bg-red-50 text-red-600 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">{v.reason}</span>
              <span className="text-xs text-slate-500">{v.action}</span>
              <button data-testid={`sec-violation-ban-${i}`} disabled={busy}
                onClick={() => run(() => api.post("/admin/security/ban", { ip: v.ip, hours: 24, reason: `manuale da violazione ${v.reason}` }), t("admin.saved"))}
                className="ml-auto rounded-full border border-slate-200 px-3.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-red-400 hover:text-red-500 transition-colors">
                {t("sec.addBan")}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
